import {
  Observable,
  catchError,
  defer,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import {
  CollectionSessionCredentials,
  CollectionSessionStorage,
  StoredCollectionSessionV2,
} from './collection-session-storage';
import { CollectionWebLock } from './collection-web-lock';

export interface CollectionSessionRefreshResponse {
  readonly access_token: string;
  readonly session_id: string;
  readonly expires_in: number;
}

export interface CollectionSessionRefresher {
  refresh(
    snapshot: StoredCollectionSessionV2,
  ): Observable<CollectionSessionRefreshResponse>;
}

export class CollectionSessionUnavailableError extends Error {
  constructor(collectionId: string) {
    super(`No usable collection session exists for ${collectionId}.`);
    this.name = 'CollectionSessionUnavailableError';
  }
}

export class CollectionSessionRefreshContractError extends Error {
  constructor() {
    super('The collection session refresh response is invalid.');
    this.name = 'CollectionSessionRefreshContractError';
  }
}

export class CollectionSessionSupersededError extends Error {
  constructor(collectionId: string) {
    super(
      `The collection session operation was superseded for ${collectionId}.`,
    );
    this.name = 'CollectionSessionSupersededError';
  }
}

class CollectionSessionRefreshRequired extends Error {}

export class CollectionSessionCoordinator {
  constructor(
    private readonly sessions: CollectionSessionStorage,
    private readonly locks: CollectionWebLock,
    private readonly refresher: CollectionSessionRefresher,
    private readonly now: () => number = Date.now,
  ) {}

  captureRevision(collectionId: string): Observable<number | null> {
    return this.locks.withShared(collectionId, () =>
      defer(() => of(this.sessions.readState(collectionId).revision)),
    );
  }

  hasUsableSession(collectionId: string): Observable<boolean> {
    return this.locks.withShared(collectionId, () =>
      defer(() => {
        const snapshot = this.sessions.read(collectionId);
        return of(snapshot !== null && !this.isExpired(snapshot));
      }),
    );
  }

  publishSession(
    collectionId: string,
    expectedRevision: number | null,
    credentials: CollectionSessionCredentials,
  ): Observable<StoredCollectionSessionV2> {
    return this.locks.withExclusive(collectionId, () =>
      defer(() => {
        const current = this.sessions.readState(collectionId);
        if (current.revision !== expectedRevision) {
          if (current.session) return of(current.session);
          return throwError(
            () => new CollectionSessionSupersededError(collectionId),
          );
        }
        const published = this.sessions.compareAndSet(
          collectionId,
          expectedRevision,
          credentials,
        );
        if (!published) {
          return throwError(
            () => new CollectionSessionSupersededError(collectionId),
          );
        }
        return of(published);
      }),
    );
  }

  invalidateCapturedSession(
    collectionId: string,
    capturedRevision: number,
  ): Observable<boolean> {
    return this.locks.withExclusive(collectionId, () =>
      defer(() =>
        of(this.sessions.compareAndClear(collectionId, capturedRevision)),
      ),
    );
  }

  runWithValidSession<T>(
    collectionId: string,
    work: (snapshot: StoredCollectionSessionV2) => Observable<T>,
  ): Observable<T> {
    return this.runUnderSharedLock(collectionId, work).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof CollectionSessionRefreshRequired)) {
          return throwError(() => error);
        }

        return this.locks
          .withExclusive(collectionId, () =>
            this.refreshIfStillExpired(collectionId),
          )
          .pipe(switchMap(() => this.runUnderSharedLock(collectionId, work)));
      }),
    );
  }

  refreshAfterUnauthorized<T>(
    collectionId: string,
    failedRevision: number,
    work: (snapshot: StoredCollectionSessionV2) => Observable<T>,
  ): Observable<T> {
    return this.locks
      .withExclusive(collectionId, () =>
        this.refreshIfRevisionMatches(collectionId, failedRevision),
      )
      .pipe(switchMap(() => this.runUnderSharedLock(collectionId, work)));
  }

  private runUnderSharedLock<T>(
    collectionId: string,
    work: (snapshot: StoredCollectionSessionV2) => Observable<T>,
  ): Observable<T> {
    return this.locks.withShared(collectionId, () =>
      defer(() => {
        const snapshot = this.requireSession(collectionId);
        if (this.isExpired(snapshot)) {
          return throwError(() => new CollectionSessionRefreshRequired());
        }
        return work(snapshot);
      }),
    );
  }

  private refreshIfStillExpired(collectionId: string): Observable<void> {
    return defer(() => {
      const captured = this.requireSession(collectionId);
      if (!this.isExpired(captured)) return of(undefined);
      return this.refreshCaptured(collectionId, captured);
    });
  }

  private refreshIfRevisionMatches(
    collectionId: string,
    failedRevision: number,
  ): Observable<void> {
    return defer(() => {
      const captured = this.requireSession(collectionId);
      if (captured.revision !== failedRevision) return of(undefined);
      return this.refreshCaptured(collectionId, captured);
    });
  }

  private refreshCaptured(
    collectionId: string,
    captured: StoredCollectionSessionV2,
  ): Observable<void> {
    return this.refresher.refresh(captured).pipe(
      map((response) => {
        if (!isRefreshResponse(response)) {
          throw new CollectionSessionRefreshContractError();
        }

        this.sessions.compareAndSet(collectionId, captured.revision, {
          access_token: response.access_token,
          session_id: response.session_id,
          expires_at_epoch_ms: this.now() + response.expires_in * 1_000,
        });
        return undefined;
      }),
    );
  }

  private requireSession(collectionId: string): StoredCollectionSessionV2 {
    const snapshot = this.sessions.read(collectionId);
    if (!snapshot) throw new CollectionSessionUnavailableError(collectionId);
    return snapshot;
  }

  private isExpired(snapshot: StoredCollectionSessionV2): boolean {
    return snapshot.expires_at_epoch_ms <= this.now();
  }
}

function isRefreshResponse(
  response: CollectionSessionRefreshResponse,
): response is CollectionSessionRefreshResponse {
  return (
    isNonEmptyString(response?.access_token) &&
    isNonEmptyString(response?.session_id) &&
    Number.isFinite(response?.expires_in) &&
    response.expires_in > 0
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
